import React, { useState, useMemo } from 'react';
import { useRequests } from '../../hooks/use-requests';
import { useNavigate } from '@tanstack/react-router';
import { format, isValid, parseISO } from 'date-fns';
import {
  Group,
  Text,
  Title,
  Badge,
  ActionIcon,
  Box,
  Select,
  TextInput,
  Grid,
  Stack,
} from '@mantine/core';
import { IconPlus, IconEdit, IconTrash, IconSearch, IconX } from '@tabler/icons-react';
import { RequestType } from '../../types/request-types';
import { DataTable } from '../../components/data-table/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { Request, RequestStatus } from '../../types/api';
import { GinoriCard } from '../../components/ui/GinoriCard';
import { GinoriButton } from '../../components/ui/GinoriButton';

const getStatusColor = (status: RequestStatus) => {
  switch (status) {
    case RequestStatus.New:
      return 'ginoriBlue';
    case RequestStatus.PreCampionatura:
      return 'grape';
    case RequestStatus.PreCampionaturaCompleted:
      return 'indigo';
    case RequestStatus.InProgress:
      return 'ginoriGold';
    case RequestStatus.PendingCompletion:
      return 'ginoriSilver';
    case RequestStatus.Completed:
      return 'ginoriGreen';
    case RequestStatus.Cancelled:
      return 'ginoriRed';
    case RequestStatus.Suspended:
      return 'ginoriGray';
    default:
      return 'ginoriGray';
  }
};

const getTypeLabel = (type: any) => {
  // Gestisce sia valori numerici (enum backend) che stringhe
  switch (type) {
    case 1:
    case 'sketch':
    case 'Bozzetto':
      return 'Bozzetto';
    case 2:
    case 'sample':
    case 'Campionatura':
      return 'Campionatura';
    case 3:
    case 'production':
    case 'Produzione':
      return 'Produzione';
    default:
      return type?.toString() || 'N/D';
  }
};

const getStatusLabel = (status: RequestStatus) => {
  switch (status) {
    case RequestStatus.New:
      return 'Nuova';
    case RequestStatus.PreCampionatura:
      return 'Pre-Campionatura';
    case RequestStatus.PreCampionaturaCompleted:
      return 'Pre-Campionatura Completata';
    case RequestStatus.InProgress:
      return 'In Lavorazione';
    case RequestStatus.PendingCompletion:
      return 'In Attesa di Completamento';
    case RequestStatus.Completed:
      return 'Completata';
    case RequestStatus.Cancelled:
      return 'Annullata';
    case RequestStatus.Suspended:
      return 'Sospesa';
    default:
      return 'Sconosciuto';
  }
};

// Helper function per formattare le date in modo sicuro
const formatDate = (dateValue: any): string => {
  if (!dateValue) return 'N/D';
  
  let date: Date;
  
  // Se è già un oggetto Date
  if (dateValue instanceof Date) {
    date = dateValue;
  } else {
    try {
      // Se è una stringa ISO
      date = typeof dateValue === 'string' ? parseISO(dateValue) : new Date(dateValue);
    } catch (e) {
      return 'Data non valida';
    }
  }
  
  return isValid(date) ? format(date, 'dd/MM/yyyy') : 'Data non valida';
};

export function RequestsList() {
  const navigate = useNavigate();
  
  // Usa gli hook direttamente
  const { 
    requests, 
    isLoading, 
    isError, 
    error, 
    refetch, 
    deleteMutation 
  } = useRequests();
  
  // Stati per i filtri
  const [globalFilter, setGlobalFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [clientFilter, setClientFilter] = useState('');

  // Opzioni per i filtri
  const statusOptions = [
    { value: '1', label: 'Nuova' },
    { value: '2', label: 'In Lavorazione' },
    { value: '3', label: 'In Attesa di Completamento' },
    { value: '6', label: 'Sospesa' },
    { value: '4', label: 'Completata' },
    { value: '5', label: 'Annullata' },
  ];

  const typeOptions = [
    { value: '1', label: 'Bozzetto' },
    { value: '2', label: 'Campionatura' },
    { value: '3', label: 'Produzione' },
    { value: '4', label: 'Gamma' },
  ];

  // Filtraggio dei dati
  const filteredData = useMemo(() => {
    let filtered = requests?.items || [];

    // Filtro per stato
    if (statusFilter) {
      filtered = filtered.filter(req => req.status.toString() === statusFilter);
    }

    // Filtro per tipologia
    if (typeFilter) {
      filtered = filtered.filter(req => req.requestType.toString() === typeFilter);
    }

    // Filtro per cliente
    if (clientFilter) {
      filtered = filtered.filter(req => 
        req.clientName?.toLowerCase().includes(clientFilter.toLowerCase())
      );
    }

    return filtered;
  }, [requests?.items, statusFilter, typeFilter, clientFilter]);

  const handleCreateRequest = () => {
    navigate({ to: '/requests/$requestId', params: { requestId: 'new' } });
  };

  const handleEditRequest = (id: string) => {
    navigate({ to: '/requests/$requestId', params: { requestId: id } });
  };

  const handleDeleteRequest = (id: string) => {
    if (window.confirm('Sei sicuro di voler eliminare questa richiesta?')) {
      deleteMutation.mutate(id);
    }
  };

  const columns = useMemo<ColumnDef<Request, any>[]>(
    () => [
      {
        accessorKey: 'progressiveNumberFormatted',
        header: 'N. Prog.',
        cell: ({ row }) => row.original.progressiveNumberFormatted || 'N/D',
        size: 80,
      },
      {
        accessorKey: 'clientName',
        header: 'Cliente',
        cell: ({ row }) => row.original.clientName || 'N/D',
        size: 120,
      },
      {
        accessorKey: 'title',
        header: 'Titolo',
        cell: ({ row }) => row.original.title || 'N/D',
        size: 180,
      },
      {
        accessorKey: 'requestType',
        header: 'Tipologia',
        cell: ({ row }) => getTypeLabel(row.original.requestType),
        size: 120,
      },
      {
        accessorKey: 'createdAt',
        header: 'Data Inserimento',
        cell: ({ row }) => formatDate(row.original.createdAt),
        size: 120,
      },
      {
        accessorKey: 'deliveryDate',
        header: 'Data Scadenza',
        cell: ({ row }) => row.original.deliveryDate ? formatDate(row.original.deliveryDate) : 'N/D',
        size: 120,
      },
      {
        accessorKey: 'status',
        header: 'Stato',
        cell: ({ row }) => (
          <Badge color={getStatusColor(row.original.status)}>
            {getStatusLabel(row.original.status)}
          </Badge>
        ),
        size: 120,
      },
      {
        accessorKey: 'articles',
        header: 'Articoli',
        cell: ({ row }) => `${row.original.articles?.length || 0} articoli`,
        size: 80,
      },
      {
        accessorKey: 'notes',
        header: 'Note',
        cell: ({ row }) => row.original.notes || 'N/D',
        size: 150,
      },
      {
        id: 'actions',
        header: 'Azioni',
        cell: ({ row }) => (
          <Group gap={8}>
            <ActionIcon
              color="ginoriBlue"
              variant="subtle"
              onClick={(e) => {
                e.stopPropagation();
                handleEditRequest(row.original.id!);
              }}
            >
              <IconEdit size={16} />
            </ActionIcon>
            <ActionIcon
              color="ginoriRed"
              variant="subtle"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteRequest(row.original.id!);
              }}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Group>
        ),
        size: 100,
      },
    ],
    []
  );

  if (isLoading) {
    return <Text>Caricamento...</Text>;
  }

  if (isError) {
    return <Text color="red">Errore nel caricamento delle richieste</Text>;
  }

  return (
    <Stack gap="md">
      <GinoriCard>
        <Group justify="space-between" mb="md">
          <Title order={2}>Richieste</Title>
          <GinoriButton
            leftSection={<IconPlus size={16} />}
            onClick={handleCreateRequest}
          >
            Nuova Richiesta
          </GinoriButton>
        </Group>

        {/* Filtri */}
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <TextInput
              placeholder="Cerca per cliente..."
              leftSection={<IconSearch size={16} />}
              rightSection={
                clientFilter ? (
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    onClick={() => setClientFilter('')}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                ) : null
              }
              value={clientFilter}
              onChange={(e) => setClientFilter(e.currentTarget.value)}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Select
              placeholder="Filtra per stato"
              data={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
              clearable
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Select
              placeholder="Filtra per tipologia"
              data={typeOptions}
              value={typeFilter}
              onChange={setTypeFilter}
              clearable
            />
          </Grid.Col>
        </Grid>

        {/* Indicatore risultati */}
        <Group justify="space-between" mb="md">
          <Text size="sm" c="dimmed">
            Mostrando {filteredData.length} di {requests?.items?.length || 0} richieste
          </Text>
        </Group>

        <Box>
          <DataTable
            tableId="requests-list"
            data={filteredData}
            columns={columns}
            filterValue={globalFilter}
            onFilter={setGlobalFilter}
            pagination={true}
            loading={isLoading}
            exportOptions={{
              enabled: true,
              fileName: 'richieste-ufficio-stile',
            }}
            onRowClick={(row) => handleEditRequest(row.id!)}
          />
        </Box>
      </GinoriCard>
    </Stack>
  );
}